import { useState, useEffect } from "react";
import { api } from "../api/api";
import Swal from "sweetalert2";

const BookingModal = ({ isOpen, onClose, vehicleId, user }) => {
  const [formData, setFormData] = useState({
    kilometers: "",
    pickupLocation: "",
    dropLocation: "",
    fromDate: "",
    toDate: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission
  
    // Check if toDate is before fromDate
    const fromDateObj = new Date(formData.fromDate);
    const toDateObj = new Date(formData.toDate);
    
    if (toDateObj < fromDateObj) {
      await Swal.fire({
        icon: "warning",
        title: "Invalid Date Selection",
        text: "End date cannot be before start date. Please select correct dates.",
      });
      return; // Stop the form submission
    }
    
    try {
      const response = await api.post("/bookings/bookVehicle", {
        vehicleId: vehicleId,
        userId: user._id,
        fromDate: formData.fromDate,
        toDate: formData.toDate,
        pickupLocation: formData.pickupLocation,
        dropLocation: formData.dropLocation,
        estimatedKilometers: formData.kilometers,
      });
  
      console.log("Success:", response.data);
  
      await Swal.fire({
        icon: "success",
        title: "Booking was sent to the owner!",
        text: response.data.message || "Your booking request has been submitted.",
      });
  
      onClose(); // Close the modal after the alert
    } catch (error) {
      console.error("Error:", error);
  
      await Swal.fire({
        icon: "error",
        title: "Booking Failed",
        text: error.response?.data?.message || "Something went wrong!",
      });
    }
  };
  

  // Add custom calendar styling when component mounts
  useEffect(() => {
    // Add the custom styles to enhance the date picker
    const styleEl = document.createElement('style');
    styleEl.textContent = `
      /* Custom styles for the calendar popup */
      input[type="date"]::-webkit-calendar-picker-indicator {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%236f48e5' viewBox='0 0 16 16'%3E%3Cpath d='M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z'/%3E%3C/svg%3E");
        cursor: pointer;
        opacity: 1;
        width: 20px;
        height: 20px;
      }
      
      /* Override the default browser calendar with custom styling */
      ::-webkit-datetime-edit {
        padding: 0;
      }
      
      ::-webkit-datetime-edit-fields-wrapper {
        background-color: white;
      }
      
      ::-webkit-datetime-edit-text {
        color: #6f48e5;
        padding: 0 0.2em;
      }
      
      ::-webkit-datetime-edit-month-field,
      ::-webkit-datetime-edit-day-field,
      ::-webkit-datetime-edit-year-field {
        color: #4a4a4a;
      }
      
      /* Add these styles to modify the appearance of input[type="date"] */
      input[type="date"].custom-datepicker {
        position: relative;
        color: #333;
        font-family: 'Arial', sans-serif;
      }
      
      /* The following will style the Mozilla (Firefox) calendar */
      @-moz-document url-prefix() {
        input[type="date"].custom-datepicker {
          background-color: white;
        }
      }
      
      /* Additional calendar styling for Webkit browsers */
      input[type="date"]::-webkit-inner-spin-button {
        display: none;
      }
      
      input[type="date"]:focus {
        box-shadow: 0 0 0 3px rgba(111, 72, 229, 0.25);
        border-color: #6f48e5;
        outline: none;
      }
    `;
    document.head.appendChild(styleEl);
    
    return () => {
      // Clean up by removing the style element when component unmounts
      document.head.removeChild(styleEl);
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-center items-center bg-black bg-opacity-60">
      <div className="mx-5 relative w-full max-w-md bg-white rounded-lg shadow-xl">
        {/* Modal header with WHITE background */}
        <div className="bg-white rounded-t-xl px-6 py-4 flex items-center justify-between border-b border-[#a9b9e3]">
          <h3 className="text-xl font-bold text-[#6f48e5]">
            Reserving...
          </h3>
          <button
            type="button"
            className="text-[#6f48e5] hover:bg-[#f8f5ff] rounded-full p-2 transition-colors"
            onClick={onClose}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="2" 
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        
        {/* Modal body */}
        <div className="bg-white p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-4">
              <div>
                <label className="block mb-2 text-sm font-medium text-[#6f48e5]">
                  Estimated Kilometers (km)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg 
                      className="w-5 h-5 text-[#6f48e5]" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth="2" 
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                  </div>
                  <input
                    type="number"
                    name="kilometers"
                    value={formData.kilometers}
                    onChange={handleChange}
                    className="bg-white border border-[#a9b9e3] text-gray-900 text-sm rounded-lg pl-10 focus:ring-[#6f48e5] focus:border-[#6f48e5] focus:outline-none block w-full p-2.5 transition-all"
                    placeholder="123"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-2 text-sm font-medium text-[#6f48e5]">
                    Pickup Location
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg 
                        className="w-5 h-5 text-[#6f48e5]" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth="2" 
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth="2" 
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="pickupLocation"
                      value={formData.pickupLocation}
                      onChange={handleChange}
                      className="bg-white border border-[#a9b9e3] text-gray-900 text-sm rounded-lg pl-10 focus:ring-[#6f48e5] focus:border-[#6f48e5] focus:outline-none block w-full p-2.5 transition-all"
                      placeholder="Colombo"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-[#6f48e5]">
                    Drop Location
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg 
                        className="w-5 h-5 text-[#6f48e5]" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth="2" 
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth="2" 
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="dropLocation"
                      value={formData.dropLocation}
                      onChange={handleChange}
                      className="bg-white border border-[#a9b9e3] text-gray-900 text-sm rounded-lg pl-10 focus:ring-[#6f48e5] focus:border-[#6f48e5] focus:outline-none block w-full p-2.5 transition-all"
                      placeholder="Kandy"
                      required
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block mb-2 text-sm font-medium text-[#6f48e5]">
                  Travel Dates
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-1 text-xs text-[#6f48e5]">From</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg 
                          className="w-5 h-5 text-[#6f48e5]" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth="2" 
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <input 
                        type="date" 
                        name="fromDate" 
                        value={formData.fromDate}
                        onChange={handleChange} 
                        className="custom-datepicker bg-white border border-[#a9b9e3] text-gray-900 text-sm rounded-lg pl-10 focus:ring-[#6f48e5] focus:border-[#6f48e5] focus:outline-none block w-full p-2.5 transition-all"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block mb-1 text-xs text-[#6f48e5]">To</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg 
                          className="w-5 h-5 text-[#6f48e5]" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth="2" 
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <input 
                        type="date" 
                        name="toDate" 
                        value={formData.toDate}
                        onChange={handleChange} 
                        className="custom-datepicker bg-white border border-[#a9b9e3] text-gray-900 text-sm rounded-lg pl-10 focus:ring-[#6f48e5] focus:border-[#6f48e5] focus:outline-none block w-full p-2.5 transition-all"
                        required 
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <button
              type="submit"
              className="w-full text-white bg-[#6f48e5] hover:bg-[#5e3dd0] focus:ring-4 focus:ring-[#a9b9e3] font-medium rounded-lg text-sm px-5 py-3 transition-all flex items-center justify-center"
            >
              Reserve Now
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookingModal;